import React, { useState, useEffect, useMemo } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import Icon from '../Icon';
import HelpSection from './HelpSection';
import { IBOS_STATUSES, IBOS_CALCULATION_METHODS, SEED_IBOS_LAYERS } from '../../data/ibosDefaults';
import Section105AdvisorEditor from './Section105AdvisorEditor';

export default function IBOSLayersEditor() {
  const [activeTab, setActiveTab] = useState('layers');

  return (
    <div data-testid="ibos-tabs-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Tab nav — prominent so admins can clearly see both editors live here */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: 6,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          alignSelf: 'flex-start',
        }}
      >
        <TabButton
          id="layers"
          label="IBOS Layers"
          icon="layers-outline"
          activeId={activeTab}
          onClick={setActiveTab}
        />
        <TabButton
          id="s105"
          label="Section 105 Plan Advisor"
          icon="medical-outline"
          activeId={activeTab}
          onClick={setActiveTab}
        />
      </div>

      {activeTab === 'layers' && <IBOSLayersTab />}
      {activeTab === 's105' && <Section105AdvisorEditor />}
    </div>
  );
}

function TabButton({ id, label, icon, activeId, onClick }) {
  const active = id === activeId;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`ibos-tab-${id}`}
      style={{
        padding: '10px 18px',
        background: active ? C.accent : 'transparent',
        color: active ? '#fff' : C.textMid,
        border: 'none',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        boxShadow: active ? '0 2px 6px rgba(0, 102, 204, 0.25)' : 'none',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        transition: 'background 150ms ease, color 150ms ease',
      }}
    >
      <Icon name={icon} style={{ fontSize: 15 }} />
      {label}
    </button>
  );
}

function IBOSLayersTab() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [layers, setLayers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config?.ibosLayers) {
      setLayers(JSON.parse(JSON.stringify(config.ibosLayers)));
      if (!selectedId && config.ibosLayers.length > 0) {
        setSelectedId(config.ibosLayers[0].id);
      }
    }
  }, [config?.ibosLayers]); // eslint-disable-line

  const selectedLayer = useMemo(
    () => layers.find((l) => l.id === selectedId),
    [layers, selectedId]
  );

  const updateLayer = (updater) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === selectedId ? updater(l) : l))
    );
    setHasChanges(true);
  };

  const handleReset = () => {
    if (!window.confirm('Reset all IBOS layers to defaults? This will overwrite your current customizations.')) return;
    setLayers(JSON.parse(JSON.stringify(SEED_IBOS_LAYERS)));
    setHasChanges(true);
    showToast('Reset to defaults. Click Save to persist.', 'info');
  };

  const save = async () => {
    try {
      setSaving(true);
      await updateConfig({ ibosLayers: layers });
      setHasChanges(false);
      showToast('IBOS layers saved successfully', 'success');
    } catch (err) {
      console.error('Failed to save IBOS layers:', err);
      showToast('Failed to save IBOS layers', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedLayer) {
    return (
      <div style={S.card}>
        <h2 style={S.heading}>IBOS Layer Configuration</h2>
        <p style={{ color: C.textMid }}>Loading IBOS layer defaults…</p>
      </div>
    );
  }

  return (
    <div data-testid="ibos-layers-editor" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ ...S.heading, margin: 0 }}>IBOS Layer Configuration</h2>
          <p style={{ fontSize: 12, color: C.textMid, margin: '4px 0 0' }}>
            Manage the default benefit layers, financial models, and content for all clients.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleReset}
            style={{ ...S.buttonSecondary, fontSize: 12 }}
            data-testid="ibos-editor-reset-btn"
          >
            <Icon name="refresh-outline" style={{ fontSize: 14, marginRight: 4 }} /> Reset to Defaults
          </button>
          <button
            onClick={save}
            disabled={!hasChanges || saving}
            style={{
              ...S.buttonPrimary,
              fontSize: 12,
              opacity: hasChanges && !saving ? 1 : 0.5,
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
            }}
            data-testid="ibos-editor-save-btn"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Two-column layout: layer list + layer detail */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
        {/* Layer list */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textLight }}>
            Layers
          </div>
          <div>
            {layers.map((l) => {
              const active = l.id === selectedId;
              const statusInfo = IBOS_STATUSES[l.defaultStatus] || IBOS_STATUSES.na;
              const statusColor = C[statusInfo.color] || C.textLight;
              return (
                <button
                  key={l.id}
                  onClick={() => setSelectedId(l.id)}
                  data-testid={`ibos-layer-list-${l.id}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: `1px solid ${C.borderLight}`,
                    background: active ? C.accentBg : 'transparent',
                    color: active ? C.accent : C.text,
                    fontSize: 12,
                    fontWeight: active ? 700 : 500,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ width: 24, height: 24, borderRadius: 6, background: `${statusColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={l.icon || 'cube-outline'} style={{ fontSize: 14, color: statusColor }} />
                  </span>
                  <span style={{ flex: 1 }}>
                    <div>{l.name}</div>
                    <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                      {statusInfo.label} · Layer {l.order}
                    </div>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Layer detail editor */}
        <div style={{ ...S.card }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h3 style={{ ...S.heading, margin: 0 }}>
              Layer {selectedLayer.order}: {selectedLayer.name}
            </h3>
            {selectedLayer.id === 'section125' && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: C.accentBg, color: C.accent }}>
                Computed Live
              </span>
            )}
          </div>

          {/* Basic Info */}
          <Section title="Basic Information">
            <Field label="Name">
              <input
                type="text"
                value={selectedLayer.name}
                onChange={(e) => updateLayer((l) => ({ ...l, name: e.target.value }))}
                style={S.input}
                data-testid="ibos-field-name"
              />
            </Field>
            <Field label="Short Description">
              <input
                type="text"
                value={selectedLayer.shortDescription}
                onChange={(e) => updateLayer((l) => ({ ...l, shortDescription: e.target.value }))}
                style={S.input}
              />
            </Field>
            <Field label="Long Description">
              <textarea
                value={selectedLayer.longDescription}
                onChange={(e) => updateLayer((l) => ({ ...l, longDescription: e.target.value }))}
                style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Field label="Default Status">
                <select
                  value={selectedLayer.defaultStatus}
                  onChange={(e) => updateLayer((l) => ({ ...l, defaultStatus: e.target.value }))}
                  style={S.input}
                  data-testid="ibos-field-status"
                >
                  {Object.entries(IBOS_STATUSES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Default Enabled">
                <select
                  value={selectedLayer.defaultEnabled ? 'true' : 'false'}
                  onChange={(e) => updateLayer((l) => ({ ...l, defaultEnabled: e.target.value === 'true' }))}
                  style={S.input}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>
              <Field label="Display Order">
                <input
                  type="number"
                  value={selectedLayer.order || 0}
                  onChange={(e) => updateLayer((l) => ({ ...l, order: parseInt(e.target.value) || 0 }))}
                  style={S.input}
                />
              </Field>
            </div>
          </Section>

          {/* Financial Model */}
          {selectedLayer.id !== 'section125' && (
            <Section title="Financial Model">
              <Field label="Calculation Method">
                <select
                  value={selectedLayer.calculationMethod}
                  onChange={(e) => updateLayer((l) => ({ ...l, calculationMethod: e.target.value }))}
                  style={S.input}
                  data-testid="ibos-field-calc-method"
                >
                  {IBOS_CALCULATION_METHODS.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </Field>

              {/* Inputs table */}
              <div style={{ marginTop: 10 }}>
                <div style={{ ...S.label, marginBottom: 6 }}>Inputs</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                  {Object.entries(selectedLayer.inputs || {}).map(([k, v]) => {
                    if (typeof v !== 'number') return null;
                    return (
                      <Field key={k} label={prettyLabel(k)}>
                        <input
                          type="number"
                          step="any"
                          value={v}
                          onChange={(e) =>
                            updateLayer((l) => ({
                              ...l,
                              inputs: { ...(l.inputs || {}), [k]: parseFloat(e.target.value) || 0 },
                            }))
                          }
                          style={S.input}
                          data-testid={`ibos-input-${k}`}
                        />
                      </Field>
                    );
                  })}
                </div>
              </div>
            </Section>
          )}

          {/* Compliance */}
          <Section title="Compliance">
            <Field label="Compliance Note">
              <textarea
                value={selectedLayer.complianceNote || ''}
                onChange={(e) => updateLayer((l) => ({ ...l, complianceNote: e.target.value }))}
                style={{ ...S.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, marginTop: 8 }}>
              {['legalReview', 'stateFiling', 'erisa'].map((key) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textMid }}>
                  <input
                    type="checkbox"
                    checked={!!selectedLayer.requires?.[key]}
                    onChange={(e) =>
                      updateLayer((l) => ({
                        ...l,
                        requires: { ...(l.requires || {}), [key]: e.target.checked },
                      }))
                    }
                  />
                  {prettyLabel(key)}
                </label>
              ))}
            </div>
          </Section>

          {/* Content */}
          <Section title="Report Content">
            <Field label="Bullet Points (one per line)">
              <textarea
                value={(selectedLayer.bulletPoints || []).join('\n')}
                onChange={(e) =>
                  updateLayer((l) => ({
                    ...l,
                    bulletPoints: e.target.value.split('\n').filter(Boolean),
                  }))
                }
                style={{ ...S.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
          </Section>
        </div>
      </div>

      <HelpSection title="IBOS Layer Configuration Help" storageKey="ibos-layers-help">
        <p><strong>What is IBOS?</strong> The Integrated Benefits Optimization Strategy is a multi-layer view that projects total optimization value across 8 complementary benefit layers — not just Section 125.</p>
        <p><strong>Layer 1 is special:</strong> Section 125 Cafeteria Plan always pulls its numbers live from the current Workforce analysis. You cannot edit its financial inputs here — instead, update Plan Tiers, Optimization Rules, or Revenue Commissions.</p>
        <p><strong>Calculation Methods:</strong></p>
        <ul>
          <li><strong>Per-employee monthly:</strong> Flat dollar amount × 12 × eligible count (Section 105 HRA, MEC, DPC)</li>
          <li><strong>Utilization model:</strong> Avoided ER visits × cost + perceived value (Telehealth)</li>
          <li><strong>From plan tiers:</strong> Per-enrolled annual value × eligible count (Hospitalization)</li>
          <li><strong>Rx spend model:</strong> Avg annual Rx spend × savings % × workforce (Pharmacy)</li>
          <li><strong>Absenteeism model:</strong> Days reduced × daily labor cost × workforce (Wellness)</li>
        </ul>
        <p><strong>Per-client overrides:</strong> These defaults apply to every client. Users can override per-client in the Client Drawer's IBOS Configuration section.</p>
      </HelpSection>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${C.borderLight}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ ...S.label, display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function prettyLabel(key) {
  const map = {
    monthlyContribution: 'Monthly Contribution ($)',
    visitsPerEmployeePerYear: 'Visits / Employee / Year',
    avoidedErVisitsPerYear: 'Avoided ER Visits / Year',
    avgErCost: 'Avg ER Cost ($)',
    perceivedValuePerMonth: 'Perceived Value / Month ($)',
    perEnrolledAnnualValue: 'Per-Enrolled Annual Value ($)',
    avgAnnualRxSpendPerEmployee: 'Avg Annual Rx Spend / Employee ($)',
    savingsPercentage: 'Savings Percentage (0-1)',
    employerSharePercentage: 'Employer Share Percentage (0-1)',
    absenteeismReductionDaysPerEmployee: 'Absenteeism Reduction (Days / Employee)',
    avgDailyLaborCost: 'Avg Daily Labor Cost ($)',
    roiMultiplier: 'ROI Multiplier',
    taxRate: 'Tax Rate (0-1)',
    utilizationRate: 'Utilization Rate (0-1)',
    legalReview: 'Legal Review',
    stateFiling: 'State Filing',
    erisa: 'ERISA',
    employerShare: 'Employer Share (0-1)',
    perEmployeeAnnualValue: 'Per-Employee Annual Value ($)',
  };
  return map[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}
